import { useMemo } from 'react'
import { useAuth } from '@context/AuthContext'
import { PortalKey, RoleGateConfig, UserRole } from '@/types'

const arraysEqualIgnoreOrder = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const normalizedA = [...a].sort()
  const normalizedB = [...b].sort()
  return normalizedA.every((value, index) => value === normalizedB[index])
}

export function useAuthorization() {
  const { authState } = useAuth()

  const role = authState.role
  const normalizedRole = useMemo(() => role?.toUpperCase() as UserRole | undefined, [role])
  const rawScopes = useMemo(() => authState.scopes ?? [], [authState.scopes])
  const scopes = useMemo(() => rawScopes.map(scope => scope.toLowerCase()), [rawScopes])

  const hasRole = (allowed: UserRole | UserRole[]): boolean => {
    if (!normalizedRole) return false
    const list = Array.isArray(allowed) ? allowed : [allowed]
    return list.map(item => item.toUpperCase()).includes(normalizedRole)
  }

  const hasAnyRole = (allowed: UserRole[]): boolean => {
    if (!normalizedRole) return false
    return allowed.map(item => item.toUpperCase()).includes(normalizedRole)
  }

  const hasScopes = (requiredScopes: string[] = [], { strict = false } = {}): boolean => {
    if (!requiredScopes.length) return true
    if (!scopes.length) return false

    const normalizedRequired = requiredScopes.map(scope => scope.toLowerCase())

    if (strict) {
      return arraysEqualIgnoreOrder(
        scopes.filter(scope => normalizedRequired.includes(scope)),
        normalizedRequired
      )
    }

    return normalizedRequired.every(scope => scopes.includes(scope))
  }

  const canAccessPortal = (portal: PortalKey, allowedRoles: UserRole[]): boolean => {
    if (!authState.isAuthenticated) return false
    if (!hasAnyRole(allowedRoles)) return false
    // Allow SUPER_ADMIN to access all portals even if not explicitly listed
    if (role === 'SUPER_ADMIN') return true
    return (authState.portal ?? portal) === portal
  }

  const summary = useMemo(
    () => ({
      role: normalizedRole ?? role ?? null,
      scopes: rawScopes,
      portal: authState.portal,
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.isLoading
    }),
    [authState.isAuthenticated, authState.isLoading, authState.portal, normalizedRole, rawScopes, role]
  )

  const evaluateGate = (config: RoleGateConfig) => {
    const roleOk = hasAnyRole(config.allowedRoles)
    const portalOk = config.portal === authState.portal || normalizedRole === 'SUPER_ADMIN'

    if (!roleOk) {
      return false
    }

    if (normalizedRole === 'SUPER_ADMIN') {
      return portalOk
    }

    const scopesOk = hasScopes(config.requiredScopes ?? [])
    return scopesOk && portalOk
  }

  return {
    ...summary,
    hasRole,
    hasAnyRole,
    hasScopes,
    canAccessPortal,
    evaluateGate
  }
}
